import { getErrorReporter } from "@kaggle/components";
import { featureFlags, Kaggle } from "@kaggle/sdk";
import { throttle } from "throttle-debounce";
import { getServices } from "../../../api/getServices";
import { makeSequentialSaveDraftWithQueue } from "../../editor/draft/makeSequentialSaveDraftWithQueue";
import { JupyterSource } from "../../session/JupyterSource";
import { EditorStatus } from "../../types/EditorStatus";
import { NotebookActionKey } from "../actions/ActionKeys";
import { editorErrorOccurred } from "../actions/editor";
import { stopWorker } from "../actions/worker";
import { getCurrentIpynbCells } from "../selectors/current";
import { getSourceType } from "../selectors/session";
import { AppMiddleware } from "../types";

import EditorType = Kaggle.Sdk.Kernels.EditorType;

function getKernelEditorAutosaveThrottleMs() {
  const autosaveThrottleMs = Number(
    featureFlags.kernelEditorAutosaveThrottleMs
  );
  if (isNaN(autosaveThrottleMs) || autosaveThrottleMs < 0) {
    // We should be using the FF configured value, so if we ended up returning
    // the client fallback value, log an error.
    getErrorReporter()?.report(
      new Error("Expected editor autosave throttle to use the FF config value.")
    );
    return 30000;
  }
  return autosaveThrottleMs;
}

// Structured as a function due to a dependency cycle with notebook.ts
const getSaveActionKeys = () => [
  NotebookActionKey.SET_IPYNB,
  NotebookActionKey.SET_IS_GPU_ENABLED,
  NotebookActionKey.SET_ACCELERATOR,
  NotebookActionKey.SET_IS_INTERNET_ENABLED,
  NotebookActionKey.SET_DATA_SOURCE_REFERENCES,
];

let saveDraftPending = false;
let saveDraft: () => Promise<void>;

export const saveDraftMiddleware: AppMiddleware =
  ({ getState }) =>
  next =>
  action => {
    const services = getServices();
    // Only process further if one of the specific save inducing keys is present.
    if (!getSaveActionKeys().includes(action.type)) {
      return next(action);
    }

    if (!saveDraft) {
      // Throttle calls to save draft.
      // TODO(bovard): once https://github.com/niksy/throttle-debounce/pull/46 is merged and released, use isPending() instead
      const sequentialSaveDraftWithQueue = makeSequentialSaveDraftWithQueue();
      const sequentialSaveDraftWithQueueWithPending = () => {
        saveDraftPending = false;
        return sequentialSaveDraftWithQueue();
      };
      const saveDraftThrottle = throttle(
        getKernelEditorAutosaveThrottleMs(),
        sequentialSaveDraftWithQueueWithPending
      );
      saveDraft = () => {
        saveDraftPending = true;
        return saveDraftThrottle();
      };
      window.addEventListener("beforeunload", (event: BeforeUnloadEvent) => {
        if (saveDraftPending) {
          saveDraftPending = false;
          sequentialSaveDraftWithQueue();
          const message = "Changes you made may not be saved.";
          event.returnValue = message;
          return message;
        }
        return "";
      });
    }

    // Scripts aren't backed by ipynb so there is some special handling and error conditions
    // that can be detected here prior to storing it.
    if (
      action.type === NotebookActionKey.SET_IPYNB &&
      getSourceType(getState()) === EditorType.SCRIPT
    ) {
      const source = new JupyterSource(action.payload.ipynb);
      const content = source.toNotebookContent();
      // Scripts should never have more than one cell.
      if (content.cells.length > 1) {
        const cells = content.cells;
        content.cells = [cells[0]];
        const ipynb = JupyterSource.fromNotebookContent(content).serialize();
        // tslint:disable-next-line: no-console
        console.error(
          "Script with more than one cell found. Correcting...",
          cells
        );
        action = {
          ...action,
          payload: {
            ...action.payload,
            ipynb,
          },
        };
      }
    } else if (
      action.type === NotebookActionKey.SET_IPYNB &&
      getSourceType(getState()) === EditorType.NOTEBOOK
    ) {
      const oldCells = getCurrentIpynbCells(getState());
      const newSource = new JupyterSource(action.payload.ipynb);
      const newCells = newSource.toNotebookContent().cells;

      // HACK(mbooth): For reasons we still haven't figured out, users occasionally get into a state
      // where they lose all but the first cell of their notebooks. I don't know if this would handle
      // that case either, but maybe it would prevent some of them.
      if (oldCells.length > 2 && newCells.length <= 1) {
        const confirmResult = confirm(
          "You are deleting multiple cells. If this was intentional press 'OK', " +
            "otherwise, press 'Cancel' to interrupt the action and keep those cells."
        );
        if (!confirmResult) {
          const message =
            "An error was prevented that could have led to data loss. Please refresh your kernel to continue editing.";
          services.analyticsClient.logger.reportImmediate(
            "kernels",
            "delete_many_cells_cancelled"
          );

          stopWorker();
          // EditorStatus.FATAL forces the user to refresh the browser to continue.
          editorErrorOccurred(message, EditorStatus.FATAL);
          throw new Error(message);
        } else {
          services.analyticsClient.logger.reportImmediate(
            "kernels",
            "delete_many_cells_ok"
          );
        }
      }
    }

    // Let the change commit to redux first.
    const result = next(action);

    // If it was setting it to null (initializing), don't save that.
    if (
      action.type === NotebookActionKey.SET_IPYNB &&
      action.payload.ipynb === null
    ) {
      return result;
    }

    saveDraft();
    return result;
  };
